/**
 * vore-cart-slider.js — slider horizontal do upsell "Leve tambem"
 * do cart drawer da VORE.
 *
 * Site original (vore-mirror/raw/index.html, funcao `window.initSlider`):
 *   scroll horizontal nativo + setas .slider-prev/.slider-next que rolam
 *   1 slide + barra de rolagem custom arrastavel (mouse e touch).
 *
 * Reescrito aqui como Web Component, sem jQuery e sem o swiper-bundle da CDN
 * que o site carregava dentro do drawer e nunca usava neste slider.
 *
 * CORRECOES vs. o original (registradas em VORE-DIVERGENCIAS.md):
 *   - `initSlider()` usava `document.querySelector('.slider')` etc.: so o
 *     PRIMEIRO slider da pagina funcionava e os listeners de mousemove/mouseup
 *     eram registrados de novo a cada chamada (vazamento). Aqui cada instancia
 *     resolve os proprios elementos e limpa tudo no disconnectedCallback.
 *   - O original chamava `document.addEventListener("DOMContentLoaded", fn, 2000)`
 *     — o terceiro argumento de addEventListener nao e um delay, entao o "2000"
 *     nunca fez nada. Aqui a inicializacao acontece no connectedCallback.
 *   - Setas viram `disabled` nos extremos e o thumb ganha papel de scrollbar
 *     acessivel (role/aria), que o original nao tinha.
 *
 * Markup esperado (ver snippets/vore-cart-upsell.liquid):
 *   <vore-cart-slider>
 *     <div data-vore-slider-viewport>
 *       <div data-vore-slider-track> <div class="…__slide">…</div> … </div>
 *     </div>
 *     <div><button data-vore-slider-prev>…</button><button data-vore-slider-next>…</button></div>
 *     <div data-vore-slider-scrollbar><div data-vore-slider-thumb></div></div>
 *   </vore-cart-slider>
 */

const SLIDE_GAP = 10; // px — precisa casar com o `gap` de .vore-cart-upsell__track

class VoreCartSlider extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {boolean} */
  #dragging = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragStartLeft = 0;

  /** @type {number | undefined} */
  #rafId;

  connectedCallback() {
    this.viewport = this.querySelector("[data-vore-slider-viewport]");
    this.track = this.querySelector("[data-vore-slider-track]");
    this.prevButton = this.querySelector("[data-vore-slider-prev]");
    this.nextButton = this.querySelector("[data-vore-slider-next]");
    this.scrollbar = this.querySelector("[data-vore-slider-scrollbar]");
    this.thumb = this.querySelector("[data-vore-slider-thumb]");

    if (!this.viewport || !this.track) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.prevButton?.addEventListener("click", () => this.#scrollBySlide(-1), { signal });
    this.nextButton?.addEventListener("click", () => this.#scrollBySlide(1), { signal });

    this.track.addEventListener("scroll", this.#scheduleSync, { passive: true, signal });
    window.addEventListener("resize", this.#scheduleSync, { passive: true, signal });

    if (this.thumb && this.scrollbar) {
      this.thumb.addEventListener("mousedown", this.#onDragStart, { signal });
      this.thumb.addEventListener("touchstart", this.#onDragStart, { passive: false, signal });
      document.addEventListener("mousemove", this.#onDragMove, { signal });
      document.addEventListener("touchmove", this.#onDragMove, { passive: false, signal });
      document.addEventListener("mouseup", this.#onDragEnd, { signal });
      document.addEventListener("touchend", this.#onDragEnd, { signal });
    }

    // O drawer comeca escondido: as medidas so existem quando ele abre.
    if ("ResizeObserver" in window) {
      this.observer = new ResizeObserver(this.#scheduleSync);
      this.observer.observe(this.track);
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.observer?.disconnect();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /**
   * Rola uma largura de slide (mais o gap) para a esquerda ou a direita.
   * @param {number} direction -1 ou 1
   */
  #scrollBySlide(direction) {
    const slide = this.track?.firstElementChild;
    if (!slide || !this.track) return;

    const amount = slide.clientWidth + SLIDE_GAP;
    this.track.scrollBy({ left: direction * amount, behavior: "smooth" });
  }

  #scheduleSync = () => {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#sync());
  };

  /** Recalcula tamanho e posicao do thumb e o estado das setas. */
  #sync() {
    const track = this.track;
    if (!track) return;

    const visible = track.clientWidth;
    const total = track.scrollWidth;
    const maxScroll = total - visible;

    if (this.prevButton instanceof HTMLButtonElement) {
      this.prevButton.disabled = track.scrollLeft <= 1;
    }
    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = maxScroll <= 1 || track.scrollLeft >= maxScroll - 1;
    }

    if (!this.thumb || !this.scrollbar) return;

    const railWidth = this.scrollbar.clientWidth;
    if (total <= 0 || railWidth <= 0) return;

    const thumbWidth = Math.min(railWidth, (visible / total) * railWidth);
    this.thumb.style.width = `${thumbWidth}px`;

    const maxThumbLeft = railWidth - thumbWidth;
    const ratio = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
    this.thumb.style.left = `${ratio * maxThumbLeft}px`;

    this.thumb.setAttribute("aria-valuenow", String(Math.round(ratio * 100)));
  }

  /** @param {MouseEvent | TouchEvent} event */
  #onDragStart = (event) => {
    if (!this.thumb) return;

    this.#dragging = true;
    this.#dragStartX = this.#pointerX(event);
    this.#dragStartLeft = parseFloat(window.getComputedStyle(this.thumb).left) || 0;
    event.preventDefault();
  };

  /** @param {MouseEvent | TouchEvent} event */
  #onDragMove = (event) => {
    if (!this.#dragging || !this.thumb || !this.scrollbar || !this.track) return;

    event.preventDefault();

    const delta = this.#pointerX(event) - this.#dragStartX;
    const maxThumbLeft = this.scrollbar.clientWidth - this.thumb.clientWidth;
    if (maxThumbLeft <= 0) return;

    const thumbLeft = Math.max(0, Math.min(this.#dragStartLeft + delta, maxThumbLeft));
    this.thumb.style.left = `${thumbLeft}px`;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    this.track.scrollLeft = (thumbLeft / maxThumbLeft) * maxScroll;
  };

  #onDragEnd = () => {
    this.#dragging = false;
  };

  /**
   * @param {MouseEvent | TouchEvent} event
   * @returns {number}
   */
  #pointerX(event) {
    if ("touches" in event && event.touches.length > 0) return event.touches[0].clientX;
    if ("clientX" in event) return event.clientX;
    return 0;
  }
}

if (!customElements.get("vore-cart-slider")) {
  customElements.define("vore-cart-slider", VoreCartSlider);
}

export { VoreCartSlider };
